import { OC_CENTER, OC_PRIOR_SCALE, seatWeight, type OcSens } from './oc-learn';

/**
 * The pass rate each seat of an organized crime needs (owner's report 175644-002a: "an algorithm to determine optimal
 * CPR for each role based on role weight — RegalLord is not a weak spot since the role is only 3% weight"). Pure;
 * shared by the extension (marks on Torn's Crimes tab, who-to-put-where, the OC card) and the Discord bot (low-rate
 * pings), so all judge the same way.
 *
 * The idea: each seat may cost the crime the same amount of success. In the crimes model (oc-learn.ts) a seat's pass
 * rate moves the crime's log-odds by its coefficient per 10 points, and coefficients are proportional to the role
 * weights (tornprobability's, or learned from finished crimes). Measured from a good pass rate (`good`), a seat whose
 * weight is the crime's average may drop to `low` — that is the allowed cost. A seat weighing k times the average may
 * drop only 1/k as far:
 *
 *     need = good − (good − low) × (average weight ÷ this seat's weight) × room
 *
 * so the heaviest seats need close to `good`, and a seat that barely matters (3%) can go very low — but never below
 * half of `low` (`floor`: a rate that low is a problem in any seat). `room` is the crime's own touchiness, where its
 * model has learned from finished crimes: the allowed cost is a fixed number of success points (what an average seat at
 * `low` costs a typical crime), so a crime that nearly always succeeds with good members has room (needs drop, up to
 * ×2), and one whose success swings hard with pass rates has less (needs rise, down to ×½). A crime that mostly fails
 * even with good members is judged as if it were at 50% (where every point counts most). Without learned data `room`
 * is 1.
 */

/** A good pass rate when none is given: the heaviest seats need close to it. */
export const NEED_GOOD = 75;

export interface NeedOpts {
  /** What a seat of average weight in its crime needs ("a pass rate under this is low"). */
  low: number;
  /** A good pass rate: the heaviest seats need close to it (default NEED_GOOD; never under `low`). */
  good?: number;
  /** The crime's learned model (oc-learn.ts modelSens), when there is one. */
  sens?: OcSens | null;
}

export interface SeatNeed {
  /** The pass rate this seat needs; under it is a weak spot. */
  need: number;
  /** The seat's weight (% of its crime), null when the crime's weights aren't known. */
  weight: number | null;
  /** Its weight ÷ the crime's average seat weight (1 = an average seat). */
  share: number | null;
  /** The lowest any seat may go: half of `low`. */
  floor: number;
  /** The crime's own touchiness (1 without learned data; >1 it has room, <1 a close call). */
  room: number;
  /** The learned chance of success with every seat at the good rate (learned crimes only), 0–1. */
  goodChance: number | null;
  /** Finished crimes the touchiness was learned from (0 without). */
  learnedFrom: number;
}

const sigmoid = (z: number) => 1 / (1 + Math.exp(-z));
const logit = (p: number) => Math.log(p / (1 - p));
const clamp = (x: number, lo: number, hi: number) => Math.max(lo, Math.min(hi, x));

/** The pass rate a seat ("Thief #1") needs in a crime with these weights (`{ seat key: weight }`). */
export function seatNeed(crimeWeights: Record<string, number> | null | undefined, role: string, o: NeedOpts): SeatNeed {
  const low = clamp(Number(o.low) || 0, 0, 100);
  const good = clamp(Math.max(low, Number(o.good ?? NEED_GOOD) || 0), 0, 100);
  const floor = Math.round(low / 2);
  const ws = Object.values(crimeWeights ?? {}).filter((v) => typeof v === 'number' && Number.isFinite(v) && v >= 0);
  const w = seatWeight(crimeWeights, role);
  const avg = ws.length ? ws.reduce((a, b) => a + b, 0) / ws.length : 0;
  const none: SeatNeed = { need: Math.round(low), weight: w, share: null, floor, room: 1, goodChance: null, learnedFrom: 0 };
  if (w === null || avg <= 0) return none;
  const share = w / avg;
  let room = 1;
  let goodChance: number | null = null;
  const sens = o.sens;
  if (sens && sens.scale > 0 && good > low) {
    // The allowed cost in success points: an average seat at `low` on a typical crime (the prior: no bias).
    const avgCoefRef = OC_PRIOR_SCALE / ws.length;
    const pRef = sigmoid((OC_PRIOR_SCALE * (good - OC_CENTER)) / 10);
    const cost = pRef - sigmoid(logit(pRef) - (avgCoefRef * (good - low)) / 10);
    // This crime: its chance with good members; under 50% it's treated at 50% (the steepest — every point counts).
    goodChance = sigmoid(sens.bias + (sens.scale * (good - OC_CENTER)) / 10);
    const p = Math.max(0.5, goodChance);
    const allowed = p - cost > 1e-4 ? logit(p) - logit(p - cost) : Infinity;
    const own = ((sens.scale / ws.length) * (good - low)) / 10;
    room = clamp(allowed / own, 0.5, 2);
  }
  const need = share <= 0 ? floor : good - ((good - low) * room) / share;
  return { need: Math.round(clamp(need, floor, good)), weight: w, share, floor, room, goodChance, learnedFrom: sens?.n ?? 0 };
}

const pct = (x: number) => `${Math.round(x)}%`;

/** In plain words, for a tooltip: "Thief #1 weighs 3% of this crime — any pass rate from 30% is fine here." */
export function seatNeedText(n: SeatNeed, role: string): string {
  if (n.weight === null || n.share === null) return `${role}: its weight in this crime isn’t known — it needs ${n.need}%+ (your “low” setting)`;
  const w = n.weight < 1 ? n.weight.toFixed(1) : String(Math.round(n.weight));
  const base = n.need <= n.floor ? `${role} weighs ${w}% of this crime — any pass rate from ${n.floor}% is fine here` : `${role} weighs ${w}% of this crime${n.share >= 1.5 ? ' (one of its heaviest roles)' : n.share < 0.67 ? ' (a light role)' : ''} — it needs ${n.need}%+`;
  const learned = n.goodChance !== null ? ` (learned from ${n.learnedFrom} finished crimes: with good members it succeeds about ${pct(n.goodChance * 100)}` : '';
  if (n.goodChance !== null && n.room >= 1.15) return `${base}${learned}, so it has room)`;
  if (n.goodChance !== null && n.room <= 0.87) return `${base}${learned}; pass rates move it a lot)`;
  return base;
}
